/* ═════════════════════════════════════════════════════════════════
   Vinci Institutional Banker Report — Sovereign Statement
   Elite admin console to reply to a regulated-bank compliance query.
   ═════════════════════════════════════════════════════════════════ */
import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Landmark, Loader2, FileText, RefreshCw, Trash2, Plus,
  ShieldCheck, Building2, Send, Sparkles, Archive, Mail,
  History as HistoryIcon, CheckCircle2, XCircle, Scale, AlertTriangle,
} from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const DEFAULT_CLIENT_EMAIL = "realtyf5@vinci.demo";

export function AdminBankerReport({ api }) {
  const [clientEmail, setClientEmail] = useState(DEFAULT_CLIENT_EMAIL);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [payload, setPayload] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  // Transmit modal
  const [xmitOpen, setXmitOpen] = useState(false);
  const [xmitTo, setXmitTo] = useState("");
  const [xmitCc, setXmitCc] = useState("");
  const [xmitNote, setXmitNote] = useState("");
  const [xmitBundle, setXmitBundle] = useState(true);
  const [xmitSending, setXmitSending] = useState(false);
  // History vault
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Bank Demand Mandate (Sovereign Warning)
  const [demand, setDemand] = useState({
    bank: {
      name: "Bank of Maharashtra",
      branch: "Kanjurmarg Branch, Mumbai",
      account_number: "60589820797",
      manager_name: "Ms. Kajal Baliyaan",
    },
    loss: { daily_inr: 175000, days_elapsed: 3, currency: "INR" },
    notice_period_hours: 24,
    client_impact_summary:
      "Cement + TMT-steel shipments stalled at plant gates. Driver payouts, mill remittances and site-delivery commitments deferred. Escalating demurrage and contract-penalty liability accruing every hour.",
  });
  const [altBank, setAltBank] = useState({
    beneficiary_name: "Vinci Inc.",
    bank_name: "",
    branch: "",
    account_number: "",
    ifsc: "",
    account_type: "Current",
  });
  const [demandBusy, setDemandBusy] = useState(null); // 'notice' | 'direction' | 'bundle' | null
  // Demand transmit modal
  const [dxmitOpen, setDxmitOpen] = useState(false);
  const [dxmitTo, setDxmitTo] = useState("");
  const [dxmitCc, setDxmitCc] = useState("dineshsingh.legal@gmail.com");
  const [dxmitNote, setDxmitNote] = useState("");
  const [dxmitEscalate, setDxmitEscalate] = useState(false);
  const [dxmitRoster, setDxmitRoster] = useState([]);      // [{label,email,active}]
  const [dxmitBundle, setDxmitBundle] = useState(true);
  const [dxmitSending, setDxmitSending] = useState(false);
  // Countdown ticker
  const [nowTick, setNowTick] = useState(Date.now());

  const loadPreview = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (clientEmail) qs.set("client_email", clientEmail);
      if (rangeFrom) qs.set("range_from", rangeFrom);
      if (rangeTo) qs.set("range_to", rangeTo);
      const { data } = await api.get(`/admin/banker-report/preview?${qs}`);
      setPayload(data);
      toast.success("Ledger aggregated — review & edit before generating.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(
        `/admin/banker-report/history?limit=50` +
        (clientEmail ? `&client_email=${encodeURIComponent(clientEmail)}` : "")
      );
      setHistory(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vault load failed");
    } finally { setHistoryLoading(false); }
  }, [api, clientEmail]);

  // First load = default client + history
  useEffect(() => { loadPreview(); loadHistory(); /* eslint-disable-next-line */ }, []);

  const totalFromRows = useMemo(() => {
    if (!payload?.transactions) return 0;
    return payload.transactions.reduce((a, r) => a + Number(r.amount || 0), 0);
  }, [payload]);

  const updateField = (path, val) => {
    setPayload((p) => {
      const next = JSON.parse(JSON.stringify(p));
      let cursor = next;
      const keys = path.split(".");
      for (let i = 0; i < keys.length - 1; i++) cursor = cursor[keys[i]];
      cursor[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const updateTx = (idx, key, val) => {
    setPayload((p) => {
      const next = JSON.parse(JSON.stringify(p));
      next.transactions[idx][key] = key === "amount" ? Number(val || 0) : val;
      // Re-derive aggregate totals
      next.aggregate.count = next.transactions.length;
      next.aggregate.total_inr = next.transactions.reduce(
        (a, r) => a + Number(r.amount || 0), 0);
      next.aggregate.invoice_count = new Set(
        next.transactions.map((t) => t.invoice_number)).size;
      next.aggregate.razorpay_count = next.transactions
        .filter((t) => (t.razorpay_id || "").trim()).length;
      return next;
    });
  };

  const addTx = () => {
    setPayload((p) => {
      const next = JSON.parse(JSON.stringify(p));
      next.transactions.push({
        srn: `VNC-SRN-${String(next.transactions.length + 1).padStart(6, "0")}`,
        invoice_number: "", date: new Date().toISOString().slice(0, 10),
        amount: 0, razorpay_id: "", milestones: "8 / 8",
      });
      next.aggregate.count = next.transactions.length;
      return next;
    });
  };

  const removeTx = (idx) => {
    setPayload((p) => {
      const next = JSON.parse(JSON.stringify(p));
      next.transactions.splice(idx, 1);
      next.aggregate.count = next.transactions.length;
      next.aggregate.total_inr = next.transactions.reduce(
        (a, r) => a + Number(r.amount || 0), 0);
      return next;
    });
  };

  const addVerif = () => {
    setPayload((p) => {
      const next = JSON.parse(JSON.stringify(p));
      next.verification_pack.push({
        kind: "EVIDENCE", title: "", caption: "", doc_ref: "",
      });
      return next;
    });
  };
  const updateVerif = (idx, k, v) => {
    setPayload((p) => {
      const next = JSON.parse(JSON.stringify(p));
      next.verification_pack[idx][k] = v;
      return next;
    });
  };
  const removeVerif = (idx) => {
    setPayload((p) => {
      const next = JSON.parse(JSON.stringify(p));
      next.verification_pack.splice(idx, 1);
      return next;
    });
  };

  const generatePdf = async () => {
    if (!payload) return;
    setGenerating(true);
    try {
      const res = await api.post("/admin/banker-report/generate", payload, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vinci-Institutional-Banker-Report-${payload.ref || "SOVEREIGN"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Sovereign Statement generated & downloaded.");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF generation failed");
    } finally { setGenerating(false); }
  };

  const downloadBundle = async () => {
    if (!payload) return;
    setBundling(true);
    try {
      const res = await api.post("/admin/banker-report/bundle.zip", payload, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vinci-Sovereign-Evidence-${payload.ref || "SOVEREIGN"}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Sealed evidence bundle downloaded.");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bundle build failed");
    } finally { setBundling(false); }
  };

  const openTransmit = () => {
    setXmitTo("");
    setXmitCc("dineshsingh.legal@gmail.com");
    setXmitNote("");
    setXmitBundle(true);
    setXmitOpen(true);
  };

  const doTransmit = async () => {
    if (!payload) return;
    const cc = xmitCc.split(",").map((s) => s.trim()).filter(Boolean);
    if (!xmitTo || !xmitTo.includes("@")) {
      toast.error("Enter the compliance-department email address.");
      return;
    }
    setXmitSending(true);
    try {
      const { data } = await api.post("/admin/banker-report/transmit", {
        to_email: xmitTo.trim(),
        cc,
        note: xmitNote,
        include_bundle: xmitBundle,
        payload,
      });
      if (data.ok) {
        toast.success(
          `Sent to ${data.delivered_to.join(", ")}` +
          (data.evidence_count ? ` · ${data.evidence_count} evidence file(s) bundled` : "")
        );
        setXmitOpen(false);
        loadHistory();
      } else {
        const errs = (data.failed || []).map((f) => `${f.email}: ${f.error}`).join("; ");
        toast.error(`Transmit failed. ${errs || "No inbox accepted the message."}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transmit failed");
    } finally { setXmitSending(false); }
  };

  // ── Bank Demand Mandate helpers ────────────────────────────────
  const updateDemand = (path, val) => {
    setDemand((d) => {
      const next = JSON.parse(JSON.stringify(d));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      const key = keys[keys.length - 1];
      cur[key] = (key === "daily_inr" || key === "days_elapsed" || key === "notice_period_hours")
        ? Number(val || 0) : val;
      return next;
    });
  };
  const updateAlt = (key, val) => {
    setAltBank((b) => ({ ...b, [key]: val }));
  };
  const cumulativeLoss = useMemo(
    () => (Number(demand.loss.daily_inr || 0) * Math.max(Number(demand.loss.days_elapsed || 0), 1)),
    [demand.loss]);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const generateDemandNotice = async () => {
    setDemandBusy("notice");
    try {
      const res = await api.post("/admin/banker-report/demand/generate",
        demand, { responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[:T]/g, "").slice(0, 12);
      downloadBlob(new Blob([res.data], { type: "application/pdf" }),
        `Vinci-Formal-Notice-VNC-BDM-${stamp}.pdf`);
      toast.success("Formal Notice generated.");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Formal Notice failed");
    } finally { setDemandBusy(null); }
  };

  const generateDirectionLetter = async () => {
    if (!altBank.bank_name || !altBank.account_number || !altBank.ifsc) {
      toast.error("Fill the alternate-bank name, account number and IFSC before generating the Direction Letter.");
      return;
    }
    setDemandBusy("direction");
    try {
      const res = await api.post("/admin/banker-report/direction-letter/generate",
        { bank: demand.bank, alt_bank: altBank }, { responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[:T]/g, "").slice(0, 12);
      downloadBlob(new Blob([res.data], { type: "application/pdf" }),
        `Vinci-Direction-Letter-VNC-BDL-${stamp}.pdf`);
      toast.success("Direction Letter generated.");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Direction Letter failed");
    } finally { setDemandBusy(null); }
  };

  const generateDemandBundle = async () => {
    if (!altBank.bank_name || !altBank.account_number || !altBank.ifsc) {
      toast.error("Fill the alternate-bank name, account number and IFSC before bundling.");
      return;
    }
    setDemandBusy("bundle");
    try {
      const res = await api.post("/admin/banker-report/demand-bundle.zip",
        { demand, direction: { bank: demand.bank, alt_bank: altBank } },
        { responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[:T]/g, "").slice(0, 12);
      downloadBlob(new Blob([res.data], { type: "application/zip" }),
        `Vinci-Bank-Demand-Mandate-VNC-BDM-${stamp}.zip`);
      toast.success("Sovereign Warning bundle downloaded.");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bundle build failed");
    } finally { setDemandBusy(null); }
  };

  const openDemandTransmit = async () => {
    if (!altBank.bank_name || !altBank.account_number || !altBank.ifsc) {
      toast.error("Fill the alternate-bank details before transmitting.");
      return;
    }
    setDxmitTo("");
    setDxmitCc("dineshsingh.legal@gmail.com");
    setDxmitNote("");
    setDxmitEscalate(false);
    setDxmitBundle(true);
    try {
      const { data } = await api.get("/admin/banker-report/escalation-roster");
      setDxmitRoster((data.roster || []).map((r) => ({ ...r, active: true })));
    } catch { setDxmitRoster([]); }
    setDxmitOpen(true);
  };

  const doDemandTransmit = async () => {
    if (!dxmitTo || !dxmitTo.includes("@")) {
      toast.error("Enter the Branch Manager's email address.");
      return;
    }
    const cc = dxmitCc.split(",").map((s) => s.trim()).filter(Boolean);
    const roster = dxmitEscalate
      ? dxmitRoster.filter((r) => r.active).map((r) => r.email)
      : [];
    setDxmitSending(true);
    try {
      const { data } = await api.post("/admin/banker-report/demand/transmit", {
        to_email: dxmitTo.trim(),
        cc,
        note: dxmitNote,
        escalate: dxmitEscalate,
        escalation_roster: roster,
        include_bundle: dxmitBundle,
        demand,
        direction: { bank: demand.bank, alt_bank: altBank },
      });
      if (data.ok) {
        toast.success(
          `Notice transmitted to ${data.delivered_to.join(", ")}` +
          (data.escalated ? ` · escalated to regulators` : "")
        );
        setDxmitOpen(false);
        loadHistory();
      } else {
        const errs = (data.failed || []).map((f) => `${f.email}: ${f.error}`).join("; ");
        toast.error(`Transmit failed. ${errs || "No inbox accepted the message."}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transmit failed");
    } finally { setDxmitSending(false); }
  };

  // Live countdown ticker — updates every 30 s
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const fmtCountdown = (isoDeadline) => {
    if (!isoDeadline) return null;
    const dt = new Date(isoDeadline).getTime();
    if (Number.isNaN(dt)) return null;
    const ms = dt - nowTick;
    const past = ms < 0;
    const abs = Math.abs(ms);
    const h = Math.floor(abs / 3_600_000);
    const m = Math.floor((abs % 3_600_000) / 60_000);
    return {
      label: past ? `Expired · ${h}h ${m}m ago` : `${h}h ${m}m remaining`,
      past,
      critical: !past && abs < 2 * 3_600_000,
    };
  };

  return (
    <div data-testid="admin-banker-report" className="space-y-6">
      {/* Hero strip */}
      <div className="relative overflow-hidden rounded-sm border border-[#B87333]/40 bg-gradient-to-br from-black via-[#0A0906] to-[#0F0C05] p-8">
        <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[#B87333]/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#D8B23E] inline-flex items-center gap-2">
              <Landmark className="w-3.5 h-3.5" />
              Institutional Banker Report · Sovereign Statement
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] mt-3 leading-tight">
              Reply to the Bank<br/><span className="text-[#D8B23E]">with sovereign proof.</span>
            </h2>
            <p className="font-body text-sm text-[#8A8A82] mt-4 max-w-2xl">
              Assemble every invoice, Razorpay settlement and verification anchor
              for a single client into one breathtaking multi-page PDF, addressed
              to the compliance department that raised the query.
            </p>
          </div>
          <div className="flex flex-col gap-2 min-w-[220px]">
            <div className="border border-[#B87333]/30 rounded-sm px-4 py-3 bg-black/40">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]">Signatory</div>
              <div className="font-serif text-lg text-[#F5F5F0] mt-1">Vinci Inc. — Common Seal</div>
              <div className="font-body text-[11px] text-[#8A8A82]">Sole Proprietor · Vinci Inc.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Query strip — Bank + Client + Date range */}
      <div className="border border-white/10 rounded-sm bg-[#0A0906] p-6">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#B87333] mb-4 inline-flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5" /> Query parameters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <label className="block">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82] mb-1">Client email</div>
            <input
              data-testid="banker-report-client-email"
              className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="realtyf5@vinci.demo"
            />
          </label>
          <label className="block">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82] mb-1">Range from</div>
            <input type="date"
              data-testid="banker-report-range-from"
              className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none"
              value={rangeFrom}
              onChange={(e) => setRangeFrom(e.target.value)}
            />
          </label>
          <label className="block">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82] mb-1">Range to</div>
            <input type="date"
              data-testid="banker-report-range-to"
              className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none"
              value={rangeTo}
              onChange={(e) => setRangeTo(e.target.value)}
            />
          </label>
          <div className="flex items-end">
            <button
              data-testid="banker-report-load-btn"
              onClick={loadPreview}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 border border-[#B87333] hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-4 py-2 font-grotesk text-xs uppercase tracking-[0.2em] transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {loading ? "Aggregating…" : "Aggregate ledger"}
            </button>
          </div>
        </div>
      </div>

      {!payload && !loading && (
        <div className="border border-dashed border-white/10 rounded-sm p-10 text-center font-body text-sm text-[#8A8A82]">
          Load the aggregation to review the Sovereign Statement.
        </div>
      )}

      {payload && (
        <div className="space-y-6">
          {/* Aggregate KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ["Aggregate credit", inr(totalFromRows), "text-[#D8B23E]"],
              ["Remittances", String(payload.transactions.length), "text-[#F5F5F0]"],
              ["Invoices", String(payload.aggregate.invoice_count), "text-[#F5F5F0]"],
              ["Razorpay-verified", String(payload.aggregate.razorpay_count), "text-[#2FB89A]"],
            ].map(([k, v, cls]) => (
              <motion.div key={k} whileHover={{ y: -2 }}
                className="border border-[#B87333]/20 rounded-sm bg-black/60 p-4">
                <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">{k}</div>
                <div className={`font-serif text-2xl mt-1 ${cls}`}>{v}</div>
              </motion.div>
            ))}
          </div>

          {/* Bank + Client editors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-white/10 rounded-sm bg-[#0A0906] p-5">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#B87333] mb-3">Beneficiary bank</div>
              {[
                ["Name", "bank.name"],
                ["Branch", "bank.branch"],
                ["Account number", "bank.account_number"],
                ["Kind attn", "bank.attn"],
              ].map(([lbl, path]) => (
                <label key={path} className="block mb-3">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">{lbl}</div>
                  <input
                    data-testid={`br-${path.replace(/\./g, "-")}`}
                    className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none"
                    value={path.split(".").reduce((o, k) => o[k], payload) || ""}
                    onChange={(e) => updateField(path, e.target.value)}
                  />
                </label>
              ))}
            </div>
            <div className="border border-white/10 rounded-sm bg-[#0A0906] p-5">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#B87333] mb-3">Client / Buyer</div>
              {[
                ["Name", "client.name"],
                ["GSTIN", "client.gstin"],
                ["State", "client.state"],
                ["Email on file", "client.email"],
              ].map(([lbl, path]) => (
                <label key={path} className="block mb-3">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">{lbl}</div>
                  <input
                    data-testid={`br-${path.replace(/\./g, "-")}`}
                    className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none"
                    value={path.split(".").reduce((o, k) => o[k], payload) || ""}
                    onChange={(e) => updateField(path, e.target.value)}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Transaction ledger editor */}
          <div className="border border-white/10 rounded-sm bg-[#0A0906] p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#B87333]">
                Forensic ledger · {payload.transactions.length} rows
              </div>
              <button onClick={addTx}
                data-testid="banker-report-add-tx"
                className="inline-flex items-center gap-1 border border-[#B87333]/60 hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                <Plus className="w-3.5 h-3.5" /> Add row
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#8A8A82] font-grotesk text-[9px] uppercase tracking-[0.2em]">
                    <th className="text-left py-2 px-2">VNC-SRN</th>
                    <th className="text-left py-2 px-2">Invoice #</th>
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-right py-2 px-2">Amount</th>
                    <th className="text-left py-2 px-2">Razorpay ID</th>
                    <th className="text-left py-2 px-2">Milestones</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {payload.transactions.map((tx, idx) => (
                    <tr key={idx} className="border-t border-white/5">
                      {["srn", "invoice_number", "date"].map((k) => (
                        <td key={k} className="py-1 px-2">
                          <input className="w-full bg-transparent border border-white/10 rounded-sm px-2 py-1 font-body text-xs text-[#F5F5F0] focus:border-[#B87333] outline-none"
                            value={tx[k] || ""} onChange={(e) => updateTx(idx, k, e.target.value)} />
                        </td>
                      ))}
                      <td className="py-1 px-2">
                        <input type="number" className="w-full text-right bg-transparent border border-white/10 rounded-sm px-2 py-1 font-body text-xs text-[#D8B23E] focus:border-[#B87333] outline-none"
                          value={tx.amount} onChange={(e) => updateTx(idx, "amount", e.target.value)} />
                      </td>
                      <td className="py-1 px-2">
                        <input className="w-full bg-transparent border border-white/10 rounded-sm px-2 py-1 font-body text-xs text-[#F5F5F0] focus:border-[#B87333] outline-none"
                          value={tx.razorpay_id || ""} onChange={(e) => updateTx(idx, "razorpay_id", e.target.value)} />
                      </td>
                      <td className="py-1 px-2">
                        <input className="w-full bg-transparent border border-white/10 rounded-sm px-2 py-1 font-body text-xs text-[#F5F5F0] focus:border-[#B87333] outline-none"
                          value={tx.milestones || ""} onChange={(e) => updateTx(idx, "milestones", e.target.value)} />
                      </td>
                      <td className="py-1 px-2 text-right">
                        <button onClick={() => removeTx(idx)}
                          className="text-[#8A8A82] hover:text-[#C0392B] transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-[#B87333]/40">
                    <td colSpan={3} className="py-3 px-2 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333]">
                      Aggregate total
                    </td>
                    <td className="py-3 px-2 text-right font-serif text-xl text-[#D8B23E]">{inr(totalFromRows)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Verification pack */}
          <div className="border border-white/10 rounded-sm bg-[#0A0906] p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#B87333] inline-flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Verification pack (QR-anchored)
              </div>
              <button onClick={addVerif}
                data-testid="banker-report-add-verif"
                className="inline-flex items-center gap-1 border border-[#B87333]/60 hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                <Plus className="w-3.5 h-3.5" /> Add anchor
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {payload.verification_pack.map((v, idx) => (
                <div key={idx} className="border border-white/10 rounded-sm p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Kind (e.g. AGREEMENT)"
                      className="bg-black border border-white/10 rounded-sm px-2 py-1 font-body text-xs text-[#F5F5F0] focus:border-[#B87333] outline-none"
                      value={v.kind} onChange={(e) => updateVerif(idx, "kind", e.target.value)} />
                    <input placeholder="Doc ref (for QR)"
                      className="bg-black border border-white/10 rounded-sm px-2 py-1 font-body text-xs text-[#F5F5F0] focus:border-[#B87333] outline-none"
                      value={v.doc_ref || ""} onChange={(e) => updateVerif(idx, "doc_ref", e.target.value)} />
                  </div>
                  <input placeholder="Title"
                    className="w-full mt-2 bg-black border border-white/10 rounded-sm px-2 py-1 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none"
                    value={v.title || ""} onChange={(e) => updateVerif(idx, "title", e.target.value)} />
                  <textarea placeholder="Caption"
                    className="w-full mt-2 bg-black border border-white/10 rounded-sm px-2 py-1 font-body text-xs text-[#8A8A82] focus:border-[#B87333] outline-none"
                    value={v.caption || ""} rows={2} onChange={(e) => updateVerif(idx, "caption", e.target.value)} />
                  <div className="text-right mt-2">
                    <button onClick={() => removeVerif(idx)}
                      className="text-[#8A8A82] hover:text-[#C0392B] transition-colors inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Generate CTA row — download / bundle / transmit */}
          <div className="sticky bottom-4 z-10">
            <div className="border border-[#B87333]/60 rounded-sm bg-gradient-to-r from-black via-[#0F0C05] to-black p-5 flex flex-wrap items-center justify-between gap-4 shadow-2xl">
              <div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] inline-flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> Ready to render
                </div>
                <div className="font-serif text-lg text-[#F5F5F0] mt-1">
                  Sovereign Statement · {payload.transactions.length} rows · {inr(totalFromRows)}
                </div>
                <div className="font-body text-xs text-[#8A8A82]">
                  Multi-page A4 · sealed · Vinci Inc. Common Seal — Authorized Signatory · scan-to-verify QR anchors.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={generatePdf}
                  data-testid="banker-report-generate-btn"
                  disabled={generating || bundling || xmitSending}
                  className="inline-flex items-center gap-2 border border-[#B87333] hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-4 py-3 font-grotesk text-xs uppercase tracking-[0.22em] transition-colors">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {generating ? "Rendering…" : "PDF only"}
                </button>
                <button onClick={downloadBundle}
                  data-testid="banker-report-bundle-btn"
                  disabled={generating || bundling || xmitSending}
                  className="inline-flex items-center gap-2 border border-[#B87333] hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-4 py-3 font-grotesk text-xs uppercase tracking-[0.22em] transition-colors">
                  {bundling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                  {bundling ? "Sealing…" : "Sealed evidence bundle (.zip)"}
                </button>
                <button onClick={openTransmit}
                  data-testid="banker-report-transmit-btn"
                  disabled={generating || bundling || xmitSending}
                  className="inline-flex items-center gap-2 bg-[#D8B23E] hover:bg-[#F6E4A6] text-black rounded-sm px-5 py-3 font-grotesk text-xs uppercase tracking-[0.24em] transition-colors">
                  <Mail className="w-4 h-4" /> Send to compliance dept
                </button>
              </div>
            </div>
          </div>

          {pdfUrl && (
            <div className="border border-white/10 rounded-sm bg-black p-3">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#B87333] mb-2 inline-flex items-center gap-2">
                <Send className="w-3.5 h-3.5" /> Live preview
              </div>
              <iframe title="Banker Report" src={pdfUrl}
                data-testid="banker-report-preview-iframe"
                className="w-full h-[80vh] rounded-sm border border-white/10" />
            </div>
          )}

          {/* ⚖ Sovereign Warning · Bank Demand Mandate */}
          <div className="relative overflow-hidden rounded-sm border border-[#C8CBD0]/40 bg-gradient-to-br from-[#050505] via-[#0B0B0C] to-[#0F0F10] p-8" data-testid="admin-bank-demand-panel">
            <div className="absolute -left-24 -top-24 w-72 h-72 rounded-full bg-[#8B1A1A]/20 blur-3xl pointer-events-none" />
            <div className="absolute right-6 top-6 rotate-[-14deg] border border-[#8B1A1A]/70 rounded-sm px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#F5B7B7]">
              <AlertTriangle className="w-3 h-3 inline mr-1" /> Formal Legal Notice
            </div>
            <div className="relative">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C8CBD0] inline-flex items-center gap-2">
                <Scale className="w-3.5 h-3.5" /> The Sovereign Warning · Bank Demand Mandate
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-[#FDFDFB] mt-3 leading-tight">
                Escalate the freeze.<br/><span className="text-[#C8CBD0]">Demand release. Or closure.</span>
              </h3>
              <p className="font-body text-sm text-[#8B8F96] mt-3 max-w-3xl">
                Steel/silver letterhead, board-level Executive Command seal, quantified daily-loss tabulation and a companion Direction Letter with alternate-bank RTGS mandate — signed by Vinci Inc. — Common Seal.
              </p>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8B8F96] mb-1">Estimated Daily Loss (INR)</div>
                  <input type="number" data-testid="demand-daily-loss"
                    className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#C8CBD0] outline-none"
                    value={demand.loss.daily_inr}
                    onChange={(e) => updateDemand("loss.daily_inr", e.target.value)} />
                </label>
                <label className="block">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8B8F96] mb-1">Days elapsed since freeze</div>
                  <input type="number" data-testid="demand-days-elapsed"
                    className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#C8CBD0] outline-none"
                    value={demand.loss.days_elapsed}
                    onChange={(e) => updateDemand("loss.days_elapsed", e.target.value)} />
                </label>
                <label className="block">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8B8F96] mb-1">Notice period (hours)</div>
                  <input type="number" data-testid="demand-notice-hours"
                    className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#C8CBD0] outline-none"
                    value={demand.notice_period_hours}
                    onChange={(e) => updateDemand("notice_period_hours", e.target.value)} />
                </label>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-[#8B1A1A]/40 rounded-sm p-3 bg-[#8B1A1A]/10">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#F5B7B7]">Cumulative Loss</div>
                  <div className="font-serif text-2xl text-[#FDFDFB] mt-1">
                    {inr(cumulativeLoss)}
                  </div>
                </div>
                <label className="md:col-span-2 block">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8B8F96] mb-1">Impact summary (goes into §II·A narrative)</div>
                  <textarea rows={3} data-testid="demand-impact-summary"
                    className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#C8CBD0] outline-none"
                    value={demand.client_impact_summary}
                    onChange={(e) => updateDemand("client_impact_summary", e.target.value)} />
                </label>
              </div>

              {/* Alternate bank RTGS block */}
              <div className="mt-6 border border-[#C8CBD0]/30 rounded-sm bg-black/60 p-5">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#C8CBD0] inline-flex items-center gap-2 mb-3">
                  <Building2 className="w-3.5 h-3.5" /> Alternate Beneficiary · RTGS Mandate (Direction Letter)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    ["beneficiary_name", "Beneficiary Name"],
                    ["bank_name", "Bank Name"],
                    ["branch", "Branch"],
                    ["account_number", "Account Number"],
                    ["ifsc", "IFSC Code"],
                    ["account_type", "Account Type (Current / Savings)"],
                  ].map(([k, lbl]) => (
                    <label key={k} className="block">
                      <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8B8F96] mb-1">{lbl}</div>
                      <input data-testid={`demand-alt-${k}`}
                        className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#C8CBD0] outline-none"
                        value={altBank[k] || ""}
                        onChange={(e) => updateAlt(k, k === "ifsc" ? e.target.value.toUpperCase() : e.target.value)} />
                    </label>
                  ))}
                </div>
                <div className="mt-2 font-body text-[11px] text-[#8B8F96]">
                  All three — Bank Name, Account Number and IFSC — are required to generate the Direction Letter or the sealed bundle.
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={generateDemandNotice}
                  disabled={!!demandBusy}
                  data-testid="demand-generate-notice"
                  className="inline-flex items-center gap-2 border border-[#C8CBD0] hover:bg-[#C8CBD0]/10 text-[#FDFDFB] rounded-sm px-5 py-3 font-grotesk text-xs uppercase tracking-[0.22em] transition-colors">
                  {demandBusy === "notice" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
                  {demandBusy === "notice" ? "Drafting…" : "Formal Notice (PDF)"}
                </button>
                <button onClick={generateDirectionLetter}
                  disabled={!!demandBusy}
                  data-testid="demand-generate-direction"
                  className="inline-flex items-center gap-2 border border-[#C8CBD0] hover:bg-[#C8CBD0]/10 text-[#FDFDFB] rounded-sm px-5 py-3 font-grotesk text-xs uppercase tracking-[0.22em] transition-colors">
                  {demandBusy === "direction" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {demandBusy === "direction" ? "Drafting…" : "Direction Letter (PDF)"}
                </button>
                <button onClick={generateDemandBundle}
                  disabled={!!demandBusy}
                  data-testid="demand-generate-bundle"
                  className="inline-flex items-center gap-2 border border-[#C8CBD0] hover:bg-[#C8CBD0]/10 text-[#FDFDFB] rounded-sm px-5 py-3 font-grotesk text-xs uppercase tracking-[0.24em] transition-colors">
                  {demandBusy === "bundle" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                  {demandBusy === "bundle" ? "Sealing…" : "Download both (.zip)"}
                </button>
                <button onClick={openDemandTransmit}
                  disabled={!!demandBusy}
                  data-testid="demand-transmit-open"
                  className="inline-flex items-center gap-2 bg-[#8B1A1A] hover:bg-[#B22222] text-[#FDFDFB] rounded-sm px-5 py-3 font-grotesk text-xs uppercase tracking-[0.24em] transition-colors border border-[#F5B7B7]/40">
                  <Mail className="w-4 h-4" /> Transmit to Branch Manager
                </button>
              </div>
            </div>
          </div>

          {/* Bank Response Vault */}
          <div className="border border-white/10 rounded-sm bg-[#0A0906] p-5" data-testid="banker-report-history-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#B87333] inline-flex items-center gap-2">
                <HistoryIcon className="w-3.5 h-3.5" /> Bank Response Vault
              </div>
              <button onClick={loadHistory}
                disabled={historyLoading}
                data-testid="banker-report-history-refresh"
                className="inline-flex items-center gap-1 border border-white/10 hover:border-[#B87333] text-[#C9C9C2] rounded-sm px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                {historyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Refresh
              </button>
            </div>
            {history.length === 0 ? (
              <div className="text-center py-6 font-body text-sm text-[#8A8A82]">
                No dossiers on file yet. Generate, seal or transmit one and it will appear here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#8A8A82] font-grotesk text-[9px] uppercase tracking-[0.2em]">
                      <th className="text-left py-2 px-2">When</th>
                      <th className="text-left py-2 px-2">Kind</th>
                      <th className="text-left py-2 px-2">Ref</th>
                      <th className="text-left py-2 px-2">Client</th>
                      <th className="text-left py-2 px-2">Bank / A/c</th>
                      <th className="text-right py-2 px-2">Aggregate</th>
                      <th className="text-left py-2 px-2">Countdown</th>
                      <th className="text-left py-2 px-2">Delivered / bundle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id} className="border-t border-white/5" data-testid="banker-report-history-row">
                        <td className="py-2 px-2 font-body text-xs text-[#C9C9C2]">
                          {(h.at || "").slice(0, 16).replace("T", " ")}
                          <div className="text-[10px] text-[#8A8A82]">{h.by}</div>
                        </td>
                        <td className="py-2 px-2">
                          <span className={`font-grotesk text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${h.kind === "transmit" ? "bg-[#D8B23E]/20 text-[#F6E4A6]" : h.kind === "bundle" ? "bg-[#2FB89A]/20 text-[#2FB89A]" : "bg-white/10 text-[#C9C9C2]"}`}>
                            {h.kind === "transmit" ? <Mail className="w-3 h-3 inline mr-1" /> :
                             h.kind === "bundle" ? <Archive className="w-3 h-3 inline mr-1" /> :
                             <FileText className="w-3 h-3 inline mr-1" />}
                            {h.kind}
                          </span>
                        </td>
                        <td className="py-2 px-2 font-mono text-[10px] text-[#8A8A82]">{h.ref}</td>
                        <td className="py-2 px-2 font-body text-xs text-[#F5F5F0]">
                          {h.client_name}
                          <div className="text-[10px] text-[#8A8A82]">{h.client_email}</div>
                        </td>
                        <td className="py-2 px-2 font-body text-xs text-[#F5F5F0]">
                          {h.bank_name}
                          <div className="text-[10px] text-[#8A8A82]">A/c {h.account_number}</div>
                        </td>
                        <td className="py-2 px-2 text-right font-body text-xs text-[#D8B23E]">
                          {h.kind && h.kind.startsWith("demand") ? (
                            h.cumulative_loss_inr ? (
                              <>
                                {inr(h.cumulative_loss_inr)}
                                <div className="text-[10px] text-[#8A8A82]">{h.days_elapsed || 0}d @ {inr(h.daily_loss_inr || 0)}/d</div>
                              </>
                            ) : "—"
                          ) : (
                            <>
                              {inr(h.aggregate_inr)}
                              <div className="text-[10px] text-[#8A8A82]">{h.aggregate_count} txn</div>
                            </>
                          )}
                        </td>
                        <td className="py-2 px-2 font-body text-[11px]">
                          {(() => {
                            const cd = fmtCountdown(h.notice_deadline_at);
                            if (!cd) return <span className="text-[#8A8A82]">—</span>;
                            const cls = cd.past
                              ? "text-[#C0392B]"
                              : cd.critical
                                ? "text-[#F5B7B7]"
                                : "text-[#2FB89A]";
                            return (
                              <span className={cls} data-testid="banker-report-countdown">
                                {cd.past ? "⚠ " : "⏱ "}{cd.label}
                              </span>
                            );
                          })()}
                          {h.escalated ? (
                            <div className="text-[10px] text-[#F5B7B7] mt-1 inline-flex items-center gap-1">
                              <Scale className="w-3 h-3" /> Escalated to regulators
                            </div>
                          ) : null}
                        </td>
                        <td className="py-2 px-2 font-body text-[11px] text-[#C9C9C2]">
                          {h.kind === "transmit" ? (
                            <div>
                              {(h.delivered_to || []).map((e, i) => (
                                <div key={i} className="inline-flex items-center gap-1 mr-2 text-[#2FB89A]">
                                  <CheckCircle2 className="w-3 h-3" /> {e}
                                </div>
                              ))}
                              {(h.failed || []).map((f, i) => (
                                <div key={i} className="inline-flex items-center gap-1 mr-2 text-[#C0392B]">
                                  <XCircle className="w-3 h-3" /> {f.email}
                                </div>
                              ))}
                              {h.included_bundle && (
                                <div className="text-[10px] text-[#8A8A82] mt-1">
                                  <Archive className="w-3 h-3 inline" /> {h.evidence_count || 0} evidence file(s)
                                </div>
                              )}
                            </div>
                          ) : h.kind === "bundle" ? (
                            <span>{h.evidence_count || 0} evidence · {Math.round((h.bundle_bytes || 0) / 1024)} KB</span>
                          ) : (
                            <span>PDF only</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transmit modal */}
      {xmitOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="banker-report-transmit-modal">
          <div className="relative w-full max-w-2xl border border-[#B87333]/60 rounded-sm bg-[#0A0906] p-6 shadow-2xl">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] inline-flex items-center gap-2 mb-2">
              <Mail className="w-3.5 h-3.5" /> Transmit to Compliance Dept
            </div>
            <h3 className="font-serif text-2xl text-[#F5F5F0] mb-1">Send Sovereign Statement</h3>
            <p className="font-body text-xs text-[#8A8A82] mb-4">
              Emails the PDF (and, optionally, a sealed evidence bundle) directly from the registered Vinci mailbox. Every send is logged in the Bank Response Vault.
            </p>
            <label className="block mb-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">Compliance email · TO</div>
              <input type="email" placeholder="compliance@bankofmaharashtra.co.in"
                data-testid="banker-report-transmit-to"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none"
                value={xmitTo} onChange={(e) => setXmitTo(e.target.value)} />
            </label>
            <label className="block mb-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">CC (comma-separated)</div>
              <input placeholder="dineshsingh.legal@gmail.com, cfo@vinci..."
                data-testid="banker-report-transmit-cc"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none"
                value={xmitCc} onChange={(e) => setXmitCc(e.target.value)} />
            </label>
            <label className="block mb-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">Personal note (optional)</div>
              <textarea rows={3} placeholder="A short covering note that appears inside the letter body..."
                data-testid="banker-report-transmit-note"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none"
                value={xmitNote} onChange={(e) => setXmitNote(e.target.value)} />
            </label>
            <label className="flex items-center gap-2 mb-4">
              <input type="checkbox" data-testid="banker-report-transmit-bundle-toggle"
                checked={xmitBundle} onChange={(e) => setXmitBundle(e.target.checked)} />
              <span className="font-body text-sm text-[#C9C9C2]">
                Attach sealed evidence bundle (signed MSA + video declaration if on file)
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setXmitOpen(false)} disabled={xmitSending}
                data-testid="banker-report-transmit-cancel"
                className="border border-white/20 hover:border-white/40 text-[#C9C9C2] rounded-sm px-4 py-2 font-grotesk text-xs uppercase tracking-[0.22em]">
                Cancel
              </button>
              <button onClick={doTransmit} disabled={xmitSending}
                data-testid="banker-report-transmit-send"
                className="inline-flex items-center gap-2 bg-[#D8B23E] hover:bg-[#F6E4A6] text-black rounded-sm px-5 py-2 font-grotesk text-xs uppercase tracking-[0.24em]">
                {xmitSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {xmitSending ? "Transmitting…" : "Send now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Demand transmit modal — Sovereign Warning (crimson/steel) */}
      {dxmitOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="demand-transmit-modal">
          <div className="relative w-full max-w-2xl border border-[#8B1A1A]/70 rounded-sm bg-[#0B0B0C] p-6 shadow-2xl">
            <div className="absolute right-4 top-4 rotate-[-14deg] border border-[#8B1A1A] rounded-sm px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#F5B7B7]">
              <AlertTriangle className="w-3 h-3 inline mr-1" /> Formal Legal Notice
            </div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C8CBD0] inline-flex items-center gap-2 mb-2">
              <Scale className="w-3.5 h-3.5" /> Transmit to Branch Manager
            </div>
            <h3 className="font-serif text-2xl text-[#FDFDFB] mb-1">Serve the Sovereign Warning</h3>
            <p className="font-body text-xs text-[#8B8F96] mb-4 max-w-lg">
              Emails the Formal Notice and Direction Letter with a warning-tone HTML cover letter. Every transmission is logged with a live 24-hour countdown in the Bank Response Vault.
            </p>

            <label className="block mb-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8B8F96] mb-1">Branch Manager email · TO</div>
              <input type="email" placeholder="branchmanager.kanjurmarg@bankofmaharashtra.co.in"
                data-testid="demand-transmit-to"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#C8CBD0] outline-none"
                value={dxmitTo} onChange={(e) => setDxmitTo(e.target.value)} />
            </label>
            <label className="block mb-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8B8F96] mb-1">CC (comma-separated)</div>
              <input placeholder="dineshsingh.legal@gmail.com, ..."
                data-testid="demand-transmit-cc"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#C8CBD0] outline-none"
                value={dxmitCc} onChange={(e) => setDxmitCc(e.target.value)} />
            </label>

            {/* Escalation roster */}
            <div className="mb-3 border border-[#8B1A1A]/40 rounded-sm p-3 bg-[#8B1A1A]/5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="demand-transmit-escalate"
                  checked={dxmitEscalate} onChange={(e) => setDxmitEscalate(e.target.checked)} />
                <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#F5B7B7] inline-flex items-center gap-1">
                  <Scale className="w-3 h-3" /> Also escalate to regulators (RBI Ombudsman + Consumer Forum)
                </span>
              </label>
              {dxmitEscalate && (
                <div className="mt-3 space-y-2">
                  {(dxmitRoster || []).map((r, i) => (
                    <label key={r.email} className="flex items-center gap-2 text-xs text-[#C9C9C2]">
                      <input type="checkbox" checked={r.active}
                        onChange={(e) => {
                          const next = [...dxmitRoster];
                          next[i] = { ...next[i], active: e.target.checked };
                          setDxmitRoster(next);
                        }} />
                      <span className="font-body">{r.label}</span>
                      <span className="text-[#8B8F96] font-mono">· {r.email}</span>
                    </label>
                  ))}
                  {(!dxmitRoster || dxmitRoster.length === 0) && (
                    <div className="text-[#8B8F96] text-xs font-body">Roster unavailable — will be sent to the built-in defaults.</div>
                  )}
                </div>
              )}
            </div>

            <label className="block mb-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8B8F96] mb-1">Personal note (optional · appears inside cover letter)</div>
              <textarea rows={3} placeholder="Escalating without further delay…"
                data-testid="demand-transmit-note"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#C8CBD0] outline-none"
                value={dxmitNote} onChange={(e) => setDxmitNote(e.target.value)} />
            </label>
            <label className="flex items-center gap-2 mb-5">
              <input type="checkbox" data-testid="demand-transmit-bundle"
                checked={dxmitBundle} onChange={(e) => setDxmitBundle(e.target.checked)} />
              <span className="font-body text-sm text-[#C9C9C2]">
                Attach sealed .zip bundle (both PDFs + MANIFEST)
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDxmitOpen(false)} disabled={dxmitSending}
                data-testid="demand-transmit-cancel"
                className="border border-white/20 hover:border-white/40 text-[#C9C9C2] rounded-sm px-4 py-2 font-grotesk text-xs uppercase tracking-[0.22em]">
                Cancel
              </button>
              <button onClick={doDemandTransmit} disabled={dxmitSending}
                data-testid="demand-transmit-send"
                className="inline-flex items-center gap-2 bg-[#8B1A1A] hover:bg-[#B22222] text-[#FDFDFB] rounded-sm px-5 py-2 font-grotesk text-xs uppercase tracking-[0.24em]">
                {dxmitSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {dxmitSending ? "Serving…" : "Serve notice now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
